import type { ReactNode } from 'react';
import { Compass, Lightbulb, ListOrdered, MousePointerClick } from 'lucide-react';
import { Button } from '@/design/components/Button';
import { Sheet } from '@/design/components/Dialog';
import { TermBody } from '@/design/components/Term';
import type { HelpTopic } from './helpContent';

/** "¿Cómo se usa esta pantalla?": what it is, what you can do, step by step and the words it uses. */
export function HelpSheet({
  open,
  onOpenChange,
  topic,
  onTour,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topic: HelpTopic | null;
  onTour: () => void;
}) {
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={topic ? topic.title : 'Ayuda'}
      description={topic ? 'Cómo se usa esta pantalla' : undefined}
      footer={
        <Button variant="secondary" icon={<Compass className="size-4" />} onClick={onTour}>
          Ver el recorrido de bienvenida
        </Button>
      }
    >
      {topic ? (
        <div className="space-y-6">
          <section>
            <SectionLabel icon={<Lightbulb />}>¿Qué es esto?</SectionLabel>
            <p className="text-[14.5px] leading-relaxed text-ink-900">{topic.what}</p>
          </section>

          <section>
            <SectionLabel icon={<MousePointerClick />}>¿Qué puedo hacer acá?</SectionLabel>
            <ul className="space-y-2">
              {topic.canDo.map((t) => (
                <li key={t} className="flex gap-2.5 text-[14px] leading-snug text-ink-700">
                  <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-gold-500" aria-hidden />
                  {t}
                </li>
              ))}
            </ul>
          </section>

          {topic.steps && (
            <section>
              <SectionLabel icon={<ListOrdered />}>Paso a paso</SectionLabel>
              <ol className="space-y-2.5">
                {topic.steps.map((t, i) => (
                  <li key={t} className="flex gap-3 text-[14px] leading-snug text-ink-700">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-gold-300 bg-gold-50 text-[12px] font-semibold text-gold-700">
                      {i + 1}
                    </span>
                    <span className="pt-0.5">{t}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {topic.words && topic.words.length > 0 && (
            <section>
              <div className="overline mb-2.5">Palabras que vas a ver</div>
              <div className="space-y-2">
                {topic.words.map((k) => (
                  <div key={k} className="rounded-xl border border-line bg-surface-1 p-3">
                    <TermBody k={k} />
                  </div>
                ))}
              </div>
            </section>
          )}

          <p className="rounded-xl border border-gold-200 bg-gold-50/60 p-3 text-[13px] text-ink-700">
            <strong>Un truco:</strong> las palabras con <span className="underline decoration-gold-500 decoration-dotted underline-offset-2">subrayado de puntos</span>{' '}
            se explican solas: pasá el mouse o tocalas.
          </p>
        </div>
      ) : (
        <p className="text-[14px] text-ink-700">
          Esta pantalla todavía no tiene una guía. Mirá el botón dorado: siempre te dice cuál es el siguiente paso.
        </p>
      )}
    </Sheet>
  );
}

function SectionLabel({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold tracking-wide text-gold-700 [&>svg]:size-4">
      {icon}
      {children}
    </div>
  );
}
